<template>
  <AppNavbar />
  <div class="vx-admin">
    <div style="max-width: 1040px">

      <div class="mb-3">
        <div class="vx-migas mb-1">
          <span>Suscripciones</span><span class="sep">/</span><span>Generación</span>
        </div>
        <h1 class="vx-titulo">Generar períodos</h1>
        <div class="vx-ayuda mt-1">
          Crea las {{ vocabulario.plural.toLowerCase() }} de un período para todas las
          suscripciones activas del esquema. Se previsualiza antes de confirmar.
        </div>
      </div>

      <!--
        CR-E7-004 / D28. Esta pantalla dejó de ser el segundo paso obligatorio
        del alta: ahora inscribir ya genera las cuotas. Decirlo acá arriba es
        el cambio, porque el operador que venía usándola todos los días no
        tiene forma de saber que ya no hace falta — y la va a seguir abriendo
        por las dudas.
      -->
      <div class="vx-nota mb-3">
        <i class="bi bi-info-circle"></i>
        <span>
          <strong>No hace falta venir acá después de dar de alta.</strong>
          Desde que se inscribe a alguien, sus {{ vocabulario.plural.toLowerCase() }}
          se crean solas. Esta pantalla queda para los casos de excepción:
          regenerar lo que se anuló, extender un esquema, o corregir un alta
          que se cargó con las fechas mal. Volver a generar
          <strong>no duplica</strong> lo que ya existe.
        </span>
      </div>

      <!-- 01 — Qué generar -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">01</span>
            <h2 class="vx-panel-titulo">Qué generar</h2>
          </div>
        </div>
        <div class="p-3">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="vx-etiqueta" for="g-esquema">Esquema</label>
              <select id="g-esquema" v-model="esquemaId" class="vx-campo" @change="cambiarEsquema">
                <option :value="null" disabled>Elegí un esquema…</option>
                <option v-for="e in esquemas" :key="e.cr_esquema_id" :value="e.cr_esquema_id">
                  {{ e.nombre }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="vx-etiqueta" for="g-periodo">
                {{ modo === 'RANGO' ? 'Generar hasta' : 'Período' }}
              </label>
              <select
                id="g-periodo" v-model.number="periodoNumero" class="vx-campo"
                :disabled="!periodos.length" @change="descartarPrevio"
              >
                <option :value="null" disabled>
                  {{ periodos.length ? 'Elegí un período…' : 'Elegí primero el esquema' }}
                </option>
                <option v-for="p in periodos" :key="p.numero" :value="p.numero">
                  {{ p.numero }} — {{ p.etiqueta }} (vence {{ fechaCorta(p.fecha_vencimiento) }})
                </option>
              </select>
            </div>
          </div>

          <!-- Un esquema mensual de 12 cuotas obligaba a doce vueltas por esta
               pantalla, una por mes. La regla nunca fue generar de a uno: es
               previsualizar antes de crear, y previsualizar cinco períodos
               juntos la cumple igual. -->
          <div class="d-flex gap-3 flex-wrap mt-3">
            <label
              v-for="m in MODOS" :key="m.valor"
              class="d-flex align-items-start gap-2 p-2 rounded"
              style="cursor: pointer; flex: 1 1 300px"
              :style="estiloModo(m.valor)"
            >
              <input
                v-model="modo" type="radio" :value="m.valor" class="mt-1"
                :data-test="`modo-${m.valor.toLowerCase()}`"
                @change="descartarPrevio"
              >
              <span>
                <span style="font-size: 13.5px; font-weight: 600">{{ m.titulo }}</span>
                <span class="vx-ayuda d-block">{{ m.nota }}</span>
              </span>
            </label>
          </div>

          <div v-if="!esquemas.length && !cargandoEsquemas" class="vx-nota mt-3">
            <i class="bi bi-info-circle"></i>
            <span>
              No hay esquemas de cobro cargados.
              <router-link :to="{ name: 'Esquemas' }">Creá uno primero</router-link>
              y agregale al menos un concepto.
            </span>
          </div>

          <div class="d-flex justify-content-end mt-3">
            <button
              class="vx-btn vx-btn-primario"
              data-test="btn-previsualizar"
              :disabled="calculando"
              @click="previsualizar"
            >
              <span v-if="calculando" class="spinner-border spinner-border-sm"></span>
              {{ calculando ? 'Calculando…' : 'Previsualizar' }}
            </button>
          </div>
          <div v-if="faltaElegir" class="vx-error text-end mt-2">{{ faltaElegir }}</div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger py-2 px-3 mb-3" style="font-size: 13.5px" role="alert">
        {{ error }}
      </div>

      <!-- 02 — Qué va a pasar -->
      <div v-if="previo" class="vx-panel mb-3" data-test="panel-previsualizacion">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">02</span>
            <h2 class="vx-panel-titulo">Qué va a pasar</h2>
          </div>
          <span class="vx-ayuda">{{ tituloPrevio }}</span>
        </div>

        <div class="p-3">
          <div class="row g-2 mb-3">
            <div v-for="c in cifras" :key="c.etiqueta" class="col-6 col-lg-3">
              <div class="vx-panel px-3 py-2" style="background: var(--vx-panel-alt)">
                <div class="vx-ayuda">{{ c.etiqueta }}</div>
                <div class="vx-num" style="font-size: 24px; font-weight: 650; letter-spacing: -0.02em">
                  {{ c.valor }}
                </div>
              </div>
            </div>
          </div>

          <!-- Nada para crear no es un error: es que ya se generó (RN-17). Se
               dice con todas las letras, porque un operador que ve "0" sin
               explicación vuelve a apretar el botón. -->
          <div v-if="!previo.resumen.cantidad" class="vx-nota" data-test="nada-para-crear">
            <i class="bi bi-check2-circle"></i>
            <span v-if="previo.omitidas">
              No hay nada para crear: las {{ previo.omitidas }}
              {{ previo.omitidas === 1 ? 'cuota ya estaba generada' : 'cuotas ya estaban generadas' }}
              para este período. Volver a generar no las duplica.
            </span>
            <span v-else>
              No hay nada para crear. Puede ser que el esquema no tenga conceptos activos,
              o que ninguna suscripción activa esté vigente en este período.
            </span>
          </div>

          <div v-if="previo.omitidas && previo.resumen.cantidad" class="vx-aviso mb-3">
            <div class="vx-aviso-titulo">
              <i class="bi bi-arrow-repeat"></i>
              {{ previo.omitidas }} ya {{ previo.omitidas === 1 ? 'existe' : 'existen' }}
            </div>
            <div class="vx-ayuda mt-1">
              Se omiten. Este período ya se generó, entero o en parte, y volver a
              confirmarlo no duplica nada.
            </div>
          </div>

          <!-- Las suscripciones sin responsable no rompen la generación: se
               reportan para que alguien las arregle (RN-5). Esconderlas sería
               generar de menos sin avisar. -->
          <div v-if="previo.sin_responsable.length" class="vx-aviso mb-3"
               style="border-color: rgba(191,122,0,0.35); background: rgba(191,122,0,0.05)">
            <div class="vx-aviso-titulo" style="color: #8a5a00">
              <i class="bi bi-exclamation-triangle"></i>
              {{ previo.sin_responsable.length }}
              {{ previo.sin_responsable.length === 1 ? 'suscripción se saltea' : 'suscripciones se saltean' }}
            </div>
            <div class="vx-ayuda mt-1">
              No tienen responsable de pago vigente, así que no se sabe a quién cobrarles.
              Se generan las demás.
            </div>
            <div class="d-flex flex-wrap gap-1 mt-2">
              <router-link
                v-for="id in previo.sin_responsable" :key="id"
                class="vx-chip vx-num"
                :to="{ name: 'SuscripcionDetalle', params: { id } }"
              >
                #{{ id }}
              </router-link>
            </div>
          </div>

          <!-- Con varios períodos, qué mes aporta cuánto. Un total suelto no
               deja ver que un mes quedó en cero por error de configuración. -->
          <div v-if="previo.periodos.length > 1" class="vx-panel mb-3" style="overflow-x: auto">
            <table class="vx-tabla" data-test="tabla-periodos">
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Vence</th>
                  <th class="vx-col-num">A crear</th>
                  <th class="vx-col-num">Ya estaban</th>
                  <th class="vx-col-num">Monto</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in previo.periodos" :key="p.numero">
                  <td class="vx-num">{{ p.etiqueta }}</td>
                  <td class="vx-num">{{ fechaCorta(p.fecha_vencimiento) }}</td>
                  <td class="vx-col-num vx-num">{{ p.cantidad }}</td>
                  <td class="vx-col-num vx-num">{{ p.omitidas || '—' }}</td>
                  <td class="vx-col-num vx-num">{{ formatearMonto(p.monto) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <template v-if="previo.deudas.length">
            <div class="d-flex align-items-baseline justify-content-between mb-2">
              <span class="vx-etiqueta mb-0">Muestra</span>
              <span class="vx-ayuda">
                Primeras {{ previo.deudas.length }} de {{ previo.resumen.cantidad }}
              </span>
            </div>
            <div class="vx-panel" style="overflow-x: auto">
              <table class="vx-tabla">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Responsable</th>
                    <th class="vx-col-num">Original</th>
                    <th class="vx-col-num">Descuento</th>
                    <th class="vx-col-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(d, i) in previo.deudas" :key="i">
                    <td>
                      <div class="vx-celda-principal">{{ d.descripcion }}</div>
                      <div class="vx-celda-sub vx-num">Suscripción #{{ d.cr_suscripcion_id }}</div>
                    </td>
                    <td class="vx-num vx-ayuda">#{{ d.cliente_id }}</td>
                    <td class="vx-col-num vx-num">{{ formatearMonto(d.monto_original) }}</td>
                    <td class="vx-col-num vx-num">
                      {{ Number(d.monto_descuento) ? `− ${formatearMonto(d.monto_descuento)}` : '—' }}
                    </td>
                    <td class="vx-col-num vx-num" style="font-weight: 600">
                      {{ formatearMonto(d.monto_total) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="vx-btn" :disabled="confirmando" @click="descartarPrevio">Descartar</button>
            <button
              v-if="previo.resumen.cantidad"
              class="vx-btn vx-btn-primario"
              data-test="btn-confirmar"
              :disabled="confirmando"
              @click="confirmar"
            >
              <span v-if="confirmando" class="spinner-border spinner-border-sm"></span>
              {{ confirmando
                ? 'Generando…'
                : `Confirmar ${previo.resumen.cantidad} ${previo.resumen.cantidad === 1 ? 'cuota' : 'cuotas'}` }}
            </button>
          </div>
        </div>
      </div>

      <!-- 03 — Qué pasó -->
      <div v-if="resultado" class="vx-panel" data-test="panel-resultado">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">03</span>
            <h2 class="vx-panel-titulo">Resultado</h2>
          </div>
        </div>
        <div class="p-3">
          <div class="vx-aviso">
            <div class="vx-aviso-titulo">
              <i class="bi bi-check2-circle"></i>
              <span data-test="resultado-titulo">
                {{ resultado.creadas }}
                {{ resultado.creadas === 1 ? 'cuota creada' : 'cuotas creadas' }}
                <template v-if="resultado.omitidas">
                  · {{ resultado.omitidas }}
                  {{ resultado.omitidas === 1 ? 'omitida' : 'omitidas' }}
                </template>
              </span>
            </div>
            <div class="vx-ayuda mt-1">
              {{ resultado.esquema.nombre }} ·
              {{ resultado.periodos && resultado.periodos.length > 1
                ? `${resultado.periodos.length} períodos`
                : (resultado.periodo ? resultado.periodo.etiqueta : '') }} ·
              {{ formatearMonto(resultado.resumen.monto_total) }} en total.
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="vx-btn" @click="previsualizar">Previsualizar de nuevo</button>
            <router-link class="vx-btn" :to="{ name: 'Suscripciones' }">
              Ir a suscripciones
            </router-link>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
// Generación de un período, en dos pasos — CR-E2-006.
// SPEC RN-17, RN-18 · AMB-6 · CF5.
//
// Por qué dos pasos y no un botón: confirmar crea plata a cobrar para cientos
// de personas de una sola vez. Ver antes cuántas, por cuánto y cuántas se
// omiten es lo que convierte un botón peligroso en una decisión informada.

import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import { useNotificationStore } from '@/stores/notifications'
import {
  listarEsquemas, obtenerEsquema, previsualizarGeneracion, confirmarGeneracion,
  obtenerVocabulario, VOCABULARIO_POR_DEFECTO, formatearMonto, fechaCorta
} from '@/services/cobranzaService'

const route = useRoute()
const notificaciones = useNotificationStore()

const MODOS = [
  {
    valor: 'RANGO',
    titulo: 'Todo lo pendiente hasta ese período',
    nota: 'Crea de una vez lo que falte. Lo ya generado se omite: no se duplica.'
  },
  {
    valor: 'UNO',
    titulo: 'Sólo ese período',
    nota: 'Genera únicamente el mes elegido.'
  }
]

const esquemas = ref([])
const cargandoEsquemas = ref(true)
// Por defecto el rango: es lo que un esquema de doce cuotas necesita.
const modo = ref('RANGO')
const periodos = ref([])
const esquemaId = ref(null)
const periodoNumero = ref(null)
const previo = ref(null)
const resultado = ref(null)
const calculando = ref(false)
const confirmando = ref(false)
const error = ref('')
const intentado = ref(false)

const etiquetas = ref({ ...VOCABULARIO_POR_DEFECTO })
const vocabulario = computed(() => ({
  singular: etiquetas.value['deuda.singular'],
  plural: etiquetas.value['deuda.plural']
}))

// `periodo_hasta` le dice al backend "todo lo que falte hasta acá";
// `periodo_numero`, "sólo ese".
const cuerpoDelPedido = () => (modo.value === 'RANGO'
  ? { cr_esquema_id: esquemaId.value, periodo_hasta: periodoNumero.value }
  : { cr_esquema_id: esquemaId.value, periodo_numero: periodoNumero.value })

const faltaElegir = computed(() => {
  if (!intentado.value) return ''
  if (!esquemaId.value) return 'Elegí el esquema de cobro.'
  if (!periodoNumero.value) {
    return modo.value === 'RANGO'
      ? 'Elegí hasta qué período generar.'
      : 'Elegí el período a generar.'
  }
  return ''
})

const estiloModo = (v) => modo.value === v
  ? { border: '1.5px solid var(--vx-acento)', background: 'rgba(7,167,225,0.05)' }
  : { border: '1px solid var(--vx-borde)' }

const tituloPrevio = computed(() => {
  if (!previo.value) return ''
  const p = previo.value.periodos
  if (!p.length) return previo.value.esquema.nombre
  return p.length === 1
    ? `${previo.value.esquema.nombre} · ${p[0].etiqueta} · vence ${fechaCorta(p[0].fecha_vencimiento)}`
    : `${previo.value.esquema.nombre} · ${p[0].etiqueta} a ${p[p.length - 1].etiqueta} · ${p.length} períodos`
})

const cifras = computed(() => {
  if (!previo.value) return []
  const r = previo.value.resumen
  return [
    { etiqueta: `${vocabulario.value.plural} a crear`, valor: r.cantidad },
    { etiqueta: 'Suscripciones', valor: r.suscripciones },
    { etiqueta: 'Monto total', valor: formatearMonto(r.monto_total) },
    { etiqueta: 'Descuentos', valor: formatearMonto(r.monto_descuento) }
  ]
})

// Cualquier cambio de selección INVALIDA la previsualización. Sin esto se
// puede previsualizar marzo, cambiar el select a abril y confirmar creyendo
// que se confirma lo que está en pantalla.
function descartarPrevio () {
  previo.value = null
  resultado.value = null
  error.value = ''
}

async function cambiarEsquema () {
  descartarPrevio()
  periodoNumero.value = null
  periodos.value = []
  if (!esquemaId.value) return
  try {
    const r = await obtenerEsquema(esquemaId.value)
    periodos.value = r?.periodos || []
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron leer los períodos del esquema.'
  }
}

async function previsualizar () {
  intentado.value = true
  if (!esquemaId.value || !periodoNumero.value) return

  calculando.value = true
  error.value = ''
  resultado.value = null
  try {
    previo.value = await previsualizarGeneracion(cuerpoDelPedido())
  } catch (err) {
    previo.value = null
    error.value = err?.userMessage || 'No se pudo previsualizar la generación.'
  } finally {
    calculando.value = false
  }
}

async function confirmar () {
  if (!previo.value) return
  confirmando.value = true
  error.value = ''
  try {
    // Se manda lo que la previsualización calculó, NO lo que hay en los
    // selects: es la garantía de que se confirma exactamente lo que se vio.
    const r = await confirmarGeneracion({
      cr_esquema_id: previo.value.esquema.cr_esquema_id,
      ...(previo.value.periodos.length > 1
        ? { periodo_hasta: previo.value.hasta }
        : { periodo_numero: previo.value.periodos[0].numero })
    })
    resultado.value = r
    previo.value = null
    notificaciones.notify({
      type: 'success',
      message: `${r.creadas} ${r.creadas === 1 ? 'cuota generada' : 'cuotas generadas'}.`
    })
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo generar el período.'
  } finally {
    confirmando.value = false
  }
}

onMounted(async () => {
  etiquetas.value = await obtenerVocabulario()
  try {
    esquemas.value = await listarEsquemas()
    // Se puede llegar desde el detalle de un esquema: en ese caso ya viene
    // elegido y el operador arranca directo en el período.
    const desdeRuta = Number(route.query.esquema) || null
    if (desdeRuta && esquemas.value.some((e) => e.cr_esquema_id === desdeRuta)) {
      esquemaId.value = desdeRuta
      await cambiarEsquema()
    } else if (esquemas.value.length === 1) {
      esquemaId.value = esquemas.value[0].cr_esquema_id
      await cambiarEsquema()
    }
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron cargar los esquemas.'
  } finally {
    cargandoEsquemas.value = false
  }
})
</script>
